import { useCallback, useMemo, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { formatDisplayDateRange, generateId } from "../utils/helpers";
import { isDateTooSoon } from "../utils/validation";
import type { BookingForm, LessonDate, Participant } from "../types/booking";

const bookingPersonSchema = z
  .object({
    name: z.string().min(1, "Name is required").max(100),
    email: z
      .string()
      .email({ message: "Please enter a valid email address" })
      .max(100),
    confirmEmail: z
      .string()
      .min(1, "Confirm Email is required")
      .email({ message: "Please enter a valid email address" })
      .max(100),
    hotelId: z.string().min(1, "Please select your accommodation"),
    comments: z.string().max(1000).optional(),
    agentId: z.string().optional(),
    referenceNo: z.string().max(20).optional(),
  })
  .refine(
    (data) => data.confirmEmail.toLowerCase() === data.email.toLowerCase(),
    {
      path: ["confirmEmail"],
      message: "Email and Confirm Email must match",
    },
  );

const participantSchema = z.object({
  id: z.string(),
  name: z.string().min(1, "Participant name is required").max(100),
  age: z.number().min(3, "Age must be 3 or older").max(12, "Age must be 12 or under").optional(),
  activity: z.enum(["Ski", "Snowboard", ""]).refine((val) => val !== "", {
    message: "Please select your Ski or Snowboard",
  }),
  lessonType: z.string().min(1, "Please select a lesson type"),
  timeSlot: z.enum(["AM", "PM", "FD"]),
  skillLevel: z.string().min(1, "Please select your level of ability"),
  hasLevelMismatch: z.boolean().optional(),
})
  .refine((data) => !data.hasLevelMismatch, {
    message: "Level 6 is not selectable for group lessons",
    path: ["skillLevel"],
  })
  .refine((data) => !data.hasLevelMismatch, {
    message: "Level 6 is not selectable for group lessons",
    path: ["lessonType"],
  });

const lessonDateSchema = z.object({
  id: z.string(),
  date: z
    .union([z.date(), z.null()])
    .refine((value): value is Date => value instanceof Date, {
      message: "Date of lesson is required",
    })
    .refine((value) => !value || !isDateTooSoon(value), {
      message:
        "Online bookings must be made at least 2 days in advance. Please select a later date or contact us directly.",
    }),
  participants: z
    .array(participantSchema)
    .min(1, "At least one participant is required"),
});

const bookingFormSchema = z.object({
  captchaToken: z.string().min(1, "Captcha is required"),
  acceptedTerms: z.boolean().refine((value) => value === true, {
    message: "You must accept the Terms & Conditions",
  }),
  bookingPerson: bookingPersonSchema,
  lessonDates: z
    .array(lessonDateSchema)
    .min(1, "At least one lesson date is required"),
});

type LessonDateRange = {
  startDate: Date;
  endDate: Date;
};

const toStartOfDay = (value?: string | null): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  parsed.setHours(0, 0, 0, 0);
  return parsed;
};

const calculateLessonDateRange = (
  participants: Participant[],
): { range: LessonDateRange | null; hasOverlap: boolean } => {
  const ranges = participants
    .map((participant) => participant.lessonTypeAvailability)
    .filter(
      (
        availability,
      ): availability is NonNullable<Participant["lessonTypeAvailability"]> =>
        Boolean(availability?.periodStart && availability?.periodEnd),
    )
    .map((availability) => {
      const startDate = toStartOfDay(availability.periodStart);
      const endDate = toStartOfDay(availability.periodEnd);
      if (!startDate || !endDate) {
        return null;
      }
      return { startDate, endDate };
    })
    .filter((range): range is LessonDateRange => Boolean(range));

  if (!ranges.length) {
    return { range: null, hasOverlap: true };
  }

  const startDate = ranges.reduce<Date | null>(
    (latest, current) =>
      !latest || current.startDate > latest ? current.startDate : latest,
    null,
  );

  const endDate = ranges.reduce<Date | null>(
    (earliest, current) =>
      !earliest || current.endDate < earliest ? current.endDate : earliest,
    null,
  );

  if (!startDate || !endDate) {
    return { range: null, hasOverlap: true };
  }

  return {
    range: { startDate, endDate },
    hasOverlap: startDate <= endDate,
  };
};

const NO_OVERLAP_MESSAGE =
  "No overlapping availability for the selected lesson types. Please adjust the lesson types or remove a participant.";

export const useBookingForm = () => {
  const [currentStep, setCurrentStep] = useState(0);

  const createInitialLessonDates = (): LessonDate[] => [
    {
      id: generateId(),
      date: null,
      participants: [
        {
          id: generateId(),
          name: "",
          age: undefined,
          activity: "",
          lessonType: "",
          timeSlot: "AM",
          skillLevel: "",
          lessonTypeAvailability: undefined,
        },
      ],
    },
  ];

  const [lessonDates, setLessonDates] = useState<LessonDate[]>(
    createInitialLessonDates(),
  );

  const form = useForm<BookingForm>({
    resolver: zodResolver(bookingFormSchema),
    defaultValues: {
      captchaToken: "",
      acceptedTerms: false,
      bookingPerson: {
        name: "",
        email: "",
        confirmEmail: "",
        hotelId: "",
        comments: "",
        agentId: "",
        referenceNo: "",
      },
      lessonDates,
    },
    mode: "onChange",
    reValidateMode: "onChange",
    shouldFocusError: false,
    shouldUnregister: false,
    shouldUseNativeValidation: false,
    criteriaMode: "all",
  });

  // Update form values when lessonDates change
  useEffect(() => {
    form.setValue("lessonDates", lessonDates);
  }, [lessonDates, form]);

  const enforceLessonDateConstraints = useCallback(
    (lessonDate: LessonDate, lessonIndex: number): LessonDate => {
      const fieldPath = `lessonDates.${lessonIndex}.date` as const;
      const { range, hasOverlap } = calculateLessonDateRange(
        lessonDate.participants,
      );

      if (!range) {
        form.clearErrors(fieldPath);
        return lessonDate;
      }

      if (!hasOverlap) {
        form.setError(fieldPath, {
          type: "manual",
          message: NO_OVERLAP_MESSAGE,
        });
        return lessonDate.date === null
          ? lessonDate
          : { ...lessonDate, date: null };
      }

      if (
        lessonDate.date &&
        lessonDate.date >= range.startDate &&
        lessonDate.date <= range.endDate
      ) {
        form.clearErrors(fieldPath);
        return lessonDate;
      }

      if (!lessonDate.date) {
        return lessonDate;
      }

      form.setError(fieldPath, {
        type: "manual",
        message: `Please choose a valid date for this lesson type (available from ${formatDisplayDateRange(
          range.startDate,
          range.endDate,
        )}).`,
      });

      return { ...lessonDate, date: null };
    },
    [form],
  );
  const addParticipant = useCallback(
    (lessonDateId: string) => {
      const newParticipant: Participant = {
        id: generateId(),
        name: "",
        age: undefined,
        activity: "",
        lessonType: "",
        timeSlot: "AM",
        skillLevel: "",
        lessonTypeAvailability: undefined,
      };

      setLessonDates((prev) =>
        prev.map((lessonDate, index) =>
          lessonDate.id === lessonDateId
            ? enforceLessonDateConstraints(
                {
                  ...lessonDate,
                  participants: [...lessonDate.participants, newParticipant],
                },
                index,
              )
            : lessonDate,
        ),
      );
    },
    [enforceLessonDateConstraints],
  );

  const removeParticipant = useCallback(
    (lessonDateId: string, participantId: string) => {
      setLessonDates((prev) =>
        prev.map((lessonDate, index) =>
          lessonDate.id === lessonDateId
            ? enforceLessonDateConstraints(
                {
                  ...lessonDate,
                  participants: lessonDate.participants.filter(
                    (p) => p.id !== participantId,
                  ),
                },
                index,
              )
            : lessonDate,
        ),
      );
    },
    [enforceLessonDateConstraints],
  );

  const addLessonDate = useCallback(
    (selectedDate?: Date) => {
      const lastDate = lessonDates[lessonDates.length - 1];

      const newDate = selectedDate ? new Date(selectedDate.getTime()) : null;
      if (newDate) {
        newDate.setHours(0, 0, 0, 0);
      }

      // Prefill participants from the most recent lesson's participants with new IDs
      const sourceParticipants = lastDate?.participants ?? [];
      const clonedParticipants: Participant[] =
        sourceParticipants.length > 0
          ? sourceParticipants.map((participant) => ({
              ...participant,
              id: generateId(),
              lessonTypeAvailability: participant.lessonTypeAvailability,
            }))
          : [
              {
                id: generateId(),
                name: "",
                age: undefined,
                activity: "",
                lessonType: "",
                timeSlot: "AM",
                skillLevel: "",
                lessonTypeAvailability: undefined,
              },
            ];

      const newLessonDate: LessonDate = {
        id: generateId(),
        date: newDate,
        participants: clonedParticipants,
      };

      setLessonDates((prev) => [
        ...prev,
        enforceLessonDateConstraints(newLessonDate, prev.length),
      ]);
    },
    [enforceLessonDateConstraints, lessonDates],
  );

  const removeLessonDate = useCallback(
    (lessonDateId: string) => {
      if (lessonDates.length > 1) {
        setLessonDates((prev) => prev.filter((ld) => ld.id !== lessonDateId));
      }
    },
    [lessonDates.length],
  );

  const updateLessonDate = useCallback(
    (lessonDateId: string, date: Date | null) => {
      setLessonDates((prev) =>
        prev.map((lessonDate, index) =>
          lessonDate.id === lessonDateId
            ? enforceLessonDateConstraints({ ...lessonDate, date }, index)
            : lessonDate,
        ),
      );
    },
    [enforceLessonDateConstraints],
  );

  const updateParticipant = useCallback(
    (
      lessonDateId: string,
      participantId: string,
      updates: Partial<Participant>,
    ) => {
      setLessonDates((prev) =>
        prev.map((lessonDate, index) =>
          lessonDate.id === lessonDateId
            ? enforceLessonDateConstraints(
                {
                  ...lessonDate,
                  participants: lessonDate.participants.map((p) =>
                    p.id === participantId
                      ? {
                          ...p,
                          ...updates,
                          ...(updates.lessonType === ""
                            ? { lessonTypeAvailability: undefined }
                            : {}),
                        }
                      : p,
                  ),
                },
                index,
              )
            : lessonDate,
        ),
      );
    },
    [enforceLessonDateConstraints],
  );

  const resetBookingForm = () => {
    const initialLessonDates = createInitialLessonDates();
    setLessonDates(initialLessonDates);
    form.reset({
      bookingPerson: {
        name: "",
        email: "",
        confirmEmail: "",
        hotelId: "",
        comments: "",
        agentId: "",
        referenceNo: "",
      },
      lessonDates: initialLessonDates,
      captchaToken: "",
      acceptedTerms: false,
    });
    setCurrentStep(0);
  };

  const actions = useMemo(
    () => ({
      addParticipant,
      removeParticipant,
      addLessonDate,
      removeLessonDate,
      updateLessonDate,
      updateParticipant,
      resetBookingForm,
    }),
    [
      addParticipant,
      removeParticipant,
      addLessonDate,
      removeLessonDate,
      updateLessonDate,
      updateParticipant,
    ],
  );

  return {
    form,
    currentStep,
    setCurrentStep,
    lessonDates,
    ...actions,
    schema: bookingFormSchema,
  };
};
